import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Github, Heart, Check, AlertCircle, Loader2 } from 'lucide-react';
import { subscribeToNewsletter } from '../../services/newsletter';

const Footer = () => {
  const navigate = useNavigate();
  const [emailInput, setEmailInput] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  
  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput || isSubmitting) return;
    
    setIsSubmitting(true);
    setSubscriptionStatus('idle');
    
    try {
      const success = await subscribeToNewsletter(emailInput, 'footer');
      
      if (success) {
        setSubscriptionStatus('success');
        setEmailInput('');
        // Reset success status after 3 seconds
        setTimeout(() => {
          setSubscriptionStatus('idle');
        }, 3000);
      } else {
        setSubscriptionStatus('error');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubscriptionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLegalClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/legal');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrivacyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/legal?tab=privacy');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBlogClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // First navigate to blog page
    navigate('/blog');
    
    // Use multiple approaches to ensure scrolling to top
    window.scrollTo(0, 0);
    
    // Add a small delay to ensure navigation completes before scrolling
    setTimeout(() => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    }, 100);
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/contact');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle navigation to home page sections from any page
  const handleFeaturesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    // Use setTimeout to ensure navigation completes before scrolling
    setTimeout(() => {
      const featuresSection = document.getElementById('features');
      if (featuresSection) featuresSection.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handlePricingClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      if (pricingSection) pricingSection.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleVoiceLibraryClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const voiceModelsSection = document.getElementById('voice-models');
      if (voiceModelsSection) voiceModelsSection.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const navigation = {
    product: [
      { name: 'AI Voice Generator', href: '/#features', onClick: handleFeaturesClick },
      { name: 'Text to Speech Pricing', href: '/#pricing', onClick: handlePricingClick },
      { name: 'AI Voice Library', href: '/#voice-models', onClick: handleVoiceLibraryClick },
      { name: 'Documentation', href: '#' },
    ],
    company: [
      { name: 'About QualityAIvoice', href: '/about' },
      { name: 'TTS Blog & Guides', href: '/blog', onClick: handleBlogClick },
      { name: 'Best TTS Software Guide', href: '/blog/best-text-to-speech-software-2025' },
      { name: 'Top Free TTS Tools', href: '/blog/top-10-free-text-to-speech-tools-2025' },
    ],
    support: [
      { name: 'Help Center', href: '#' },
      { name: 'Legal', href: '/legal', onClick: handleLegalClick },
      { name: 'Privacy', href: '/legal?tab=privacy', onClick: handlePrivacyClick },
      { name: 'Contact Us', href: '/contact', onClick: handleContactClick },
    ],
    social: [
      { name: 'Facebook', icon: Facebook, href: '#' },
      { name: 'Twitter', icon: Twitter, href: '#' },
      { name: 'LinkedIn', icon: Linkedin, href: '#' },
      { name: 'GitHub', icon: Github, href: '#' },
    ],
  };

  return (
    <footer className="bg-[#1a0b2e] border-t border-purple-900/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Logo and description */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">
                QualityAIvoice
              </span>
            </Link>
            <p className="mt-4 text-sm text-gray-400">
              Best text to speech software 2026. Transform text into natural-sounding speech with 500+ AI voices. Free AI voice generator for content creators, educators & businesses.
            </p>
          </div>

          {/* Product links */}
          <div>
            <h3 className="text-sm font-semibold text-purple-400 tracking-wider uppercase mb-4">
              Product
            </h3>
            <ul className="space-y-3">
              {navigation.product.map((item) => (
                <li key={item.name}>
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company links */}
          <div>
            <h3 className="text-sm font-semibold text-purple-400 tracking-wider uppercase mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {navigation.company.map((item) => (
                <li key={item.name}>
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Support links */}
          <div>
            <h3 className="text-sm font-semibold text-purple-400 tracking-wider uppercase mb-4">
              Support
            </h3>
            <ul className="space-y-3">
              {navigation.support.map((item) => (
                <li key={item.name}>
                  {item.onClick ? (
                    <button
                      onClick={item.onClick}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      to={item.href}
                      className="text-sm text-gray-400 hover:text-purple-400 transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-purple-900/20 pt-8 mb-8">
          <div className="max-w-md">
            <h3 className="text-sm font-semibold text-purple-400 tracking-wider uppercase mb-4">
              Subscribe to our newsletter
            </h3>
            <p className="text-sm text-gray-400 mb-4">
              Get the latest updates on new features and releases.
            </p>
            <form className="flex gap-2" onSubmit={handleNewsletterSubmit}>
              <input
                type="email"
                placeholder="Enter your email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                className="flex-1 px-4 py-2 rounded-lg bg-purple-900/20 border border-purple-900/30 text-gray-300 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <button 
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-blue-600 text-white font-medium hover:from-purple-700 hover:to-blue-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-w-[100px]"
              >
                {isSubmitting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : subscriptionStatus === 'success' ? (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Subscribed
                  </>
                ) : (
                  'Subscribe'
                )}
              </button>
            </form>
            {subscriptionStatus === 'error' && (
              <div className="mt-2 text-sm text-red-400 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                Failed to subscribe. Please try again.
              </div>
            )}
          </div>
        </div>

        {/* Bottom section */}
        <div className="border-t border-purple-900/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-6 mb-4 md:mb-0">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-400 hover:text-purple-400 transition-colors duration-200"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
            <div className="flex items-center text-sm text-gray-400">
              <span>Made with</span>
              <Heart className="h-4 w-4 mx-1 text-purple-500" />
              <span>by </span>
              <a href="https://senithu.lk" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 ml-1">Senithu Software Solutions</a>
              <span> © 2026</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;